import { invoke } from "@tauri-apps/api/core";
import { listen, type UnlistenFn } from "@tauri-apps/api/event";
import { getCurrentWindow } from "@tauri-apps/api/window";

import type { RoomWeather } from "../domain";
import type { UiLocale } from "../i18n/locale";
import type { AppRoute } from "../navigation/appRoute";
import {
  isTauriRuntime,
  type AppSurface,
} from "./appSurface";

const FARM_VISIBILITY_EVENT = "farm-surface-visibility";
const MARKET_REFRESH_EVENT = "market-refresh-requested";
const NAVIGATE_APP_EVENT = "navigate-app";

type ManagementRoute = Extract<
  AppRoute,
  {
    kind:
      | "portfolio-index"
      | "portfolio-new"
      | "slime-manager"
      | "observation"
      | "history-calendar"
      | "settings";
  }
>;

export type NativeNavigationKind =
  | "farm"
  | "farm-share"
  | ManagementRoute["kind"];

export interface SurfaceActivityBridge {
  listen(
    listener: (active: boolean) => void,
  ): Promise<UnlistenFn>;
  isVisible(): Promise<boolean>;
}

export interface NativeTrayMoodInput {
  readonly weather: RoomWeather;
  readonly dataCoverage: number;
  readonly holdingCount: number;
  readonly reducedMotion: boolean;
}

export interface NativeInvokeBridge {
  invoke(
    command: string,
    args?: Record<string, unknown>,
  ): Promise<unknown>;
}

const DEFAULT_SURFACE_ACTIVITY_BRIDGE: SurfaceActivityBridge = {
  listen: (listener) =>
    listen<boolean>(FARM_VISIBILITY_EVENT, ({ payload }) =>
      listener(payload),
    ),
  isVisible: () => getCurrentWindow().isVisible(),
};

const DEFAULT_INVOKE_BRIDGE: NativeInvokeBridge = {
  invoke: (command, args) => invoke(command, args),
};

export async function updateNativeTrayMood(
  input: NativeTrayMoodInput,
  bridge: NativeInvokeBridge = DEFAULT_INVOKE_BRIDGE,
): Promise<void> {
  await bridge.invoke("set_tray_mood", { input });
}

export async function syncNativeUiLocale(
  locale: UiLocale,
  bridge: NativeInvokeBridge = DEFAULT_INVOKE_BRIDGE,
  tauriRuntime = isTauriRuntime(),
): Promise<boolean> {
  if (!tauriRuntime) {
    return false;
  }

  try {
    await bridge.invoke("set_ui_locale", { locale });
    return true;
  } catch {
    // Locale selection remains a web concern when the native shell is
    // unavailable or belongs to an older app release.
    return false;
  }
}

export async function openNativeAppWindow(
  route: NativeNavigationKind,
  bridge: NativeInvokeBridge = DEFAULT_INVOKE_BRIDGE,
): Promise<void> {
  await bridge.invoke("open_portfolio_window", { route });
}

export async function openNativePortfolioWindow(
  route: Extract<
    ManagementRoute,
    { kind: "portfolio-index" | "portfolio-new" }
  >["kind"] = "portfolio-index",
): Promise<void> {
  await openNativeAppWindow(route);
}

export async function openNativeSlimeManagerWindow(): Promise<void> {
  await openNativeAppWindow("slime-manager");
}

export async function openNativeFarmShareWindow(): Promise<void> {
  await openNativeAppWindow("farm-share");
}

export async function openNativeObservationWindow(): Promise<void> {
  await openNativeAppWindow("observation");
}

export async function openNativeSettingsWindow(): Promise<void> {
  await openNativeAppWindow("settings");
}

export async function getNativeAppVersion(
  bridge: NativeInvokeBridge = DEFAULT_INVOKE_BRIDGE,
): Promise<string> {
  const version = await bridge.invoke("get_app_version");
  if (typeof version !== "string" || version.trim().length === 0) {
    throw new Error("native_app_version_unavailable");
  }
  return version;
}

export async function openNativeMacRelease(
  url: string,
  bridge: NativeInvokeBridge = DEFAULT_INVOKE_BRIDGE,
): Promise<void> {
  await bridge.invoke("open_macos_release_url", { url });
}

export function subscribeToNativeRefresh(
  listener: () => void,
): Promise<UnlistenFn> {
  return listen(MARKET_REFRESH_EVENT, listener);
}

export function decodeNativeNavigation(
  payload: string,
): NativeNavigationKind {
  if (payload === "farm") {
    return "farm";
  }
  if (payload === "farm-share") {
    return "farm-share";
  }
  if (payload === "portfolio-new") {
    return "portfolio-new";
  }
  if (payload === "slimes") {
    return "slime-manager";
  }
  if (payload === "observation") {
    return "observation";
  }
  if (payload === "calendar") {
    return "history-calendar";
  }
  if (payload === "settings") {
    return "settings";
  }
  return "portfolio-index";
}

export function subscribeToNativeNavigation(
  listener: (route: NativeNavigationKind) => void,
): Promise<UnlistenFn> {
  return listen<string>(
    NAVIGATE_APP_EVENT,
    ({ payload }) => listener(decodeNativeNavigation(payload)),
  );
}

export function subscribeToSurfaceActivity(
  surface: AppSurface,
  listener: (active: boolean) => void,
  bridge: SurfaceActivityBridge = DEFAULT_SURFACE_ACTIVITY_BRIDGE,
): () => void {
  if (surface !== "menubar") {
    listener(document.visibilityState !== "hidden");
    const onVisibilityChange = () => {
      listener(document.visibilityState !== "hidden");
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener(
        "visibilitychange",
        onVisibilityChange,
      );
  }

  let disposed = false;
  let unlisten: UnlistenFn | undefined;
  let activityRevision = 0;
  void bridge
    .listen((active) => {
      activityRevision += 1;
      if (!disposed) listener(active);
    })
    .then((nextUnlisten) => {
      if (disposed) {
        nextUnlisten();
      } else {
        unlisten = nextUnlisten;
      }
    })
    .catch(() => {
      // A direct visibility read still provides a safe initial state.
    })
    .then(async () => {
      const revisionBeforeRead = activityRevision;
      const visible = await bridge.isVisible();
      if (
        !disposed &&
        activityRevision === revisionBeforeRead
      ) {
        listener(visible);
      }
    })
    .catch(() => {
      // The initial hidden state safely keeps Pixi paused.
    });

  return () => {
    disposed = true;
    unlisten?.();
  };
}
